import { DataTableActionBarAction } from '@/components/data-table/data-table-action-bar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select'
import { bulkResumeSubscriptions } from '@/lib/api/subscriptions'
import { Info, Loader2, Play } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface BulkResumeSubscriptionsBulkActionModalProps {
    subscriptionIds: string[]
}

export const BulkResumeSubscriptionsBulkActionModal = ({
    subscriptionIds,
}: BulkResumeSubscriptionsBulkActionModalProps) => {
    const { t } = useTranslation('subscriptions')
    const { mutate: resumeSubscriptionsMutation, isPending: isResuming } = bulkResumeSubscriptions()
    const [isOpen, setIsOpen] = useState(false)
    const [resumeBehavior, setResumeBehavior] = useState<'reset' | 'preserve'>('reset')
    const [resetDays, setResetDays] = useState(0)

    const handleSubmit = () => {
        resumeSubscriptionsMutation(
            {
                subscriptionIds,
                resumeBehavior,
                resetDays: resumeBehavior === 'reset' ? resetDays : undefined,
            },
            {
                onSettled: () => {
                    setIsOpen(false)
                },
            }
        )
    }

    const handleOpenChange = (open: boolean) => {
        if (!open && !isResuming) setIsOpen(false)
    }

    return (
        <>
            <DataTableActionBarAction
                tooltip={t('bulkActions.resume.tooltip')}
                isPending={isResuming}
                onClick={() => setIsOpen(true)}
            >
                <Play className="mr-2 h-4 w-4" />
                {t('bulkActions.resume.button')}
            </DataTableActionBarAction>
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{t('bulkActions.resume.title')}</DialogTitle>
                        <DialogDescription>
                            {t('bulkActions.resume.confirmMessage', { count: subscriptionIds.length })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="resume-behavior" className="text-sm font-medium">
                                {t('bulkActions.resume.behavior')}
                            </Label>
                            <Select
                                value={resumeBehavior}
                                onValueChange={(value) => setResumeBehavior(value as 'reset' | 'preserve')}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder={t('bulkActions.resume.behavior')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="reset">{t('bulkActions.resume.reset')}</SelectItem>
                                    <SelectItem value="preserve">{t('bulkActions.resume.preserve')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Alert variant="warning" className="mt-2">
                                <Info />
                                <AlertDescription>
                                    {resumeBehavior === 'reset'
                                        ? t('bulkActions.resume.resetExplanation')
                                        : t('bulkActions.resume.preserveExplanation')}
                                </AlertDescription>
                            </Alert>
                        </div>
                        {resumeBehavior === 'reset' && (
                            <div className="space-y-2">
                                <Label htmlFor="reset-days" className="text-sm font-medium">
                                    {t('bulkActions.resume.resetDays')}
                                </Label>
                                <Input
                                    id="reset-days"
                                    type="number"
                                    min="0"
                                    max="365"
                                    value={resetDays}
                                    onChange={(e) => setResetDays(parseInt(e.target.value) || 0)}
                                    className="w-full"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIsOpen(false)} variant="outline">
                            {t('bulkActions.resume.cancel')}
                        </Button>
                        <Button onClick={handleSubmit} disabled={isResuming} variant="default">
                            {isResuming && <Loader2 className="size-4 animate-spin mr-2" />}
                            {t('bulkActions.resume.button')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}